import { Controller } from '@hotwired/stimulus'

// Connects to data-controller="auto-submit"
export default class extends Controller {
  // each of these targets will submit the form once, after a delay, when receiving 1..n 'input' event(s)
  static targets = ['debounced']

  static values = {
    // (optional) the id of the <form> element that will be auto-submitted
    // only needed if the controller is connected to something other than a <form>
    formId: { type: String, default: '' },
    // maintain current document scroll position through the form submission
    keepScrollPosition: { type: Boolean, default: false },
    // add a hidden "auto_submit_name" field to the form containing the [name] of the field that caused submission
    identifySubmitter: { type: Boolean, default: false },
  }

  connect() {
    if (this.formIdValue) {
      this.formElement = document.getElementById(this.formIdValue)
    } else {
      this.formElement = this.element
    }
    if (!(this.formElement instanceof HTMLFormElement)) {
      throw new Error(
        'This controller must either be connected to a form element, or have a valid form id value set'
      )
    }

    this.debouncedTargets.forEach((debounced) => {
      debounced.addEventListener(
        'input',
        this.debounce(() => {
          this.submit()
        })
      )
    })
  }

  submit(ev) {
    if (this.keepScrollPositionValue) {
      // https://github.com/hotwired/turbo/issues/37#issuecomment-1169195154
      document.addEventListener('turbo:render', this.keepScrollPosition, { once: true })
    }

    if (this.identifySubmitterValue && ev) {
      const fieldName = 'auto_submit_name'
      const fieldValue = ev.target.getAttribute('name')

      let submitterField = this.formElement.querySelector(`input[name=${fieldName}]`)
      if (!submitterField) {
        submitterField = document.createElement('input')
        submitterField.type = 'hidden'
        submitterField.name = fieldName
        this.formElement.appendChild(submitterField)
      }
      submitterField.value = fieldValue

      setTimeout(() => {
        submitterField.value = null
      }, 0)
    }

    // When triggered by a click on a submit button inside this form, pass it as the
    // submitter so its name/value (e.g. name="remove_contact") are included in the request,
    // and prevent the browser's native click-to-submit from also firing (double-submit race).
    // The type and containment checks are load-bearing: a synthetic `change` event from a
    // <select> inside DropdownFilterComponent's <button type="button"> trigger would
    // otherwise match closest('button') and crash requestSubmit() with "not a submit button".
    const submitter = ev?.target?.closest?.('button')
    if (submitter && submitter.type === 'submit' && this.formElement.contains(submitter)) {
      ev.preventDefault()
      this.formElement.requestSubmit(submitter)
    } else {
      this.formElement.requestSubmit()
    }
  }

  debounce(func, timeout = 300) {
    let timer
    return (...args) => {
      clearTimeout(timer)
      timer = setTimeout(() => {
        func.apply(this, args)
      }, timeout)
    }
  }

  keepScrollPosition() {
    window.Turbo.navigator.currentVisit.scrolled = true
  }
}
